// src/components/home/TestimonialsSection.js
import React, { useState, useRef, useEffect } from 'react';
import { FaQuoteLeft, FaArrowLeft, FaArrowRight, FaArrowRight as FaArrowRightLong } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import Section from '../common/Section';
import Heading from '../common/Heading';
import Button from '../common/Button';

// Testimonial data from the company profile
const testimonials = [
  {
    id: 1,
    name: "Ing. Aminata Ngaujah",
    role: "Land Surveyor, Ministry of Lands, Housing and Country Planning",
    image: "/images/testimonials/aminata_ngaujah.jpg",
    quote: "While studying Building and Civil Engineering at Milton Margai Technical University and working as a junior surveyor at the Ministry of Lands, I always felt exhausted with the traditional approach. When I heard about Leotech digital engineering design program, I was happy because I have been looking for such opportunity. These digital tools have given me advantage in my office as the youngest engineer that can use these digital platforms for quick and accurate engineering design."
  },
  {
    id: 2,
    name: "Ing. Lansana Farroh",
    role: "Civil Engineer",
    image: "/images/testimonials/lansana_farroh.jpg",
    quote: "Learning Chief Architect from Leotech has significantly enhanced my skills. This powerful design software allows me to create 3D models and detailed floor plans, facilitating better collaboration with architects and contractors. Completing my certification has boosted my confidence and enhanced my professional value, allowing me to deliver high-quality solutions and manage freelance work efficiently."
  },
  {
    id: 3,
    name: "Ing. Agnes Wurrie",
    role: "Project Engineer, SOGEFEL (SL) Limited",
    image: "https://placehold.co/100x100/1a237e/ffffff?text=AW",
    quote: "I came to know about Leotech through a friend. I always wanted to learn AutoCAD. I encourage each and everyone, especially those that have not gone through this course, to reinvent yourselves. If you don't reinvent yourselves, you will be left behind because we are talking about technology at this time."
  },
  {
    id: 4,
    name: "Ing. Deborah Momoh",
    role: "Mining Engineer, Kingho Mining Company",
    image: "https://placehold.co/100x100/1a237e/ffffff?text=DM",
    quote: "I learned about LEOTECH through a friend as I showed interest in the Chief Architect software. As an engineering student at Fourah Bay College, most of this software course is not included in our curriculum, but it is a great benefit for the career I'm embarking on. LEOTECH made it possible, and I'm grateful for that."
  },
  {
    id: 5,
    name: "Ing. Monica Musa",
    role: "IT Professional",
    image: "https://placehold.co/100x100/1a237e/ffffff?text=MM",
    quote: "Leotech's practical focus and industry connections are unbeatable. It shaped my IT career in the best way possible. Skills taught at Leotech create self-empowerment and this helped me earn my own money; which I am very sure each and every youth that goes through training will reap the same benefit."
  }
];

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for right, -1 for left
  const testimonialsRef = useRef(null);
  
  // Navigate to the next testimonial
  const nextTestimonial = () => {
    setDirection(1);
    setActiveIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };
  
  // Navigate to the previous testimonial
  const prevTestimonial = () => {
    setDirection(-1);
    setActiveIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };
  
  // Auto-advance the testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 8000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Animation variants for testimonial slides
  const slideVariants = {
    enter: (direction) => {
      return {
        x: direction > 0 ? 500 : -500,
        opacity: 0
      };
    },
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    exit: (direction) => {
      return {
        x: direction > 0 ? -500 : 500,
        opacity: 0,
        transition: {
          duration: 0.5,
          ease: "easeIn"
        }
      };
    }
  };
  
  // Create an array of dots for navigation
  const dots = Array.from({ length: testimonials.length }).map((_, i) => (
    <button
      key={i}
      onClick={() => {
        setDirection(i > activeIndex ? 1 : -1);
        setActiveIndex(i);
      }}
      className={`w-3 h-3 rounded-full mx-1 transition-all duration-300 ${
        i === activeIndex ? 'bg-primary scale-125' : 'bg-gray-300 hover:bg-gray-400'
      }`}
      aria-label={`Go to testimonial ${i + 1}`}
    />
  ));
  
  return (
    <Section background="gradient-primary" className="overflow-hidden">
      <div className="text-center mb-12">
        <Heading level={2} color="white" align="center" className="mb-4" withAccent>
          Success Stories
        </Heading>
        <p className="text-lg text-white text-opacity-90 max-w-2xl mx-auto">
          Hear from our alumni who have transformed their careers through our training programs
        </p>
      </div>
      
      <div className="max-w-6xl mx-auto relative" ref={testimonialsRef}>
        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 md:p-10 overflow-hidden">
          <FaQuoteLeft className="text-white text-opacity-20 text-6xl absolute top-4 left-4" />
          
          <div className="min-h-[350px] flex items-center justify-center">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={activeIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="w-full"
              >
                <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                  <div className="md:w-1/4 flex flex-col items-center">
                    <div className="rounded-full overflow-hidden border-4 border-white border-opacity-30 shadow-xl h-42 w-42 mb-4">
                      <img
                        src={testimonials[activeIndex].image}
                        alt={testimonials[activeIndex].name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-white text-xl font-bold text-center">
                      {testimonials[activeIndex].name}
                    </h3>
                    <p className="text-white text-opacity-80 text-center">
                      {testimonials[activeIndex].role}
                    </p>
                  </div>
                  <div className="md:w-3/4">
                    <p className="text-white text-lg italic mb-6 leading-relaxed">
                      "{testimonials[activeIndex].quote}"
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          
          <div className="flex justify-between items-center mt-8">
            <button
              onClick={prevTestimonial}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-3 transition-all duration-300"
              aria-label="Previous testimonial"
            >
              <FaArrowLeft className="h-5 w-5" />
            </button>
            
            <div className="flex items-center justify-center space-x-2">
              {dots}
            </div>
            
            <button
              onClick={nextTestimonial}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-3 transition-all duration-300"
              aria-label="Next testimonial"
            >
              <FaArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      
      <div className="text-center mt-12">
        <Button 
          variant="light" 
          size="lg" 
          to="/success-stories"
          icon={<FaArrowRightLong />}
        >
          Read More Success Stories
        </Button>
      </div>
    </Section>
  );
};

export default TestimonialsSection;